import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth as useClerkAuth, useUser, useClerk } from "@clerk/clerk-react";
import api, {
  canStoreViewAsSession,
  clearViewAsSession,
  getViewAsSessionState,
  markViewAsSessionVerified,
  setTokenGetter,
  setViewAsSession,
} from "./api";
import { appConfig } from "./appConfig";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const log = (msg, ...args) => {
  if (appConfig.debugAuthLogging) console.log(`[Auth ${new Date().toISOString()}] ${msg}`, ...args);
};

const mergeAuthUser = (data) => ({
  ...data.user,
  ...(data.gcse_subjects !== undefined ? {
    gcse_subjects: data.gcse_subjects,
    subject_source: data.subject_source ?? null,
  } : {}),
});

const viewErrorMessage = (error) =>
  error?.response?.data?.detail ||
  "The impersonation session could not be verified. Exit it before continuing.";

const unavailableView = () => ({
  target_role: "unknown",
  target: { name: "Account details unavailable" },
});
const verifiedViewMode = (viewAs) => viewAs?.mode === "act_as" ? "act_as" : "read_only";

const loadViewStatus = async () => {
  let timer;
  try {
    return await Promise.race([
      api.get("/auth/view-as/status", { timeout: 15000 }),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Viewing-session verification timed out.")), 20000);
      }),
    ]);
  } finally { clearTimeout(timer); }
};

// ─── Clerk-based auth provider ──────────────────────────────────────────────
export function ClerkAuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const clerk = useClerk();

  const [appUser, setAppUser] = useState(() => getViewAsSessionState()?.viewAs?.target || null);
  const [viewAs, setViewAs] = useState(() => {
    const stored = getViewAsSessionState();
    return stored?.token ? (stored.viewAs || unavailableView()) : null;
  });
  const [viewAsError, setViewAsError] = useState(null);
  const [viewAsVerified, setViewAsVerified] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [clerkTimedOut, setClerkTimedOut] = useState(false);
  const syncingRef = useRef(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    log("ClerkAuthProvider mounted");
  }, []);

  useEffect(() => {
    const elapsed = Date.now() - mountedAt.current;
    log(`isLoaded changed → ${isLoaded} (${elapsed}ms after mount), isSignedIn=${isSignedIn}`);
  }, [isLoaded, isSignedIn]);

  // If Clerk SDK never fires isLoaded, force loading to finish after 30 s
  useEffect(() => {
    if (isLoaded) return;
    log("Starting 30s Clerk timeout");
    const t = setTimeout(() => {
      log("TIMEOUT: Clerk isLoaded never fired after 30s — forcing syncDone");
      setClerkTimedOut(true);
      if (getViewAsSessionState()?.token) {
        setViewAsError("Clerk did not finish loading. End this view and sign in again.");
      }
      setSyncDone(true);
    }, 30000);
    return () => clearTimeout(t);
  }, [isLoaded]);

  const loading = (!isLoaded && !clerkTimedOut) || !syncDone;

  // Keep the axios interceptor's token getter in sync with Clerk
  useEffect(() => {
    setTokenGetter(isSignedIn ? getToken : null);
    return () => setTokenGetter(null);
  }, [isSignedIn, getToken]);

  const clerkUserId = clerkUser?.id;
  const syncWithBackend = useCallback(async () => {
    if (!isLoaded) return;

    const storedView = getViewAsSessionState();
    if (storedView?.token) {
      setViewAs(storedView.viewAs || unavailableView());
      setViewAsVerified(false);
      if (storedView.viewAs?.target) setAppUser(storedView.viewAs.target);
      if (!isSignedIn) {
        markViewAsSessionVerified(null);
        setViewAsError("The administrator session has expired. End this view to sign in again.");
        setSyncDone(true);
        return;
      }
      if (syncingRef.current) return;
      syncingRef.current = true;
      try {
        const { data } = await loadViewStatus();
        if (!data?.active || !data?.view_as?.target) {
          throw new Error("The server did not restore the active view.");
        }
        setViewAsSession(storedView.token, data.view_as);
        markViewAsSessionVerified(storedView.token, verifiedViewMode(data.view_as));
        setViewAs(data.view_as);
        setAppUser(data.view_as.target);
        setViewAsVerified(true);
        setViewAsError(null);
      } catch (error) {
        markViewAsSessionVerified(null);
        setViewAsVerified(false);
        log("Impersonation restore failed →", error?.response?.status, error?.message);
        setViewAsError(viewErrorMessage(error));
      } finally {
        setSyncDone(true);
        syncingRef.current = false;
      }
      return;
    }

    if (!isSignedIn) {
      log("Not signed in — skipping backend sync");
      setAppUser(null);
      setViewAs(null);
      setViewAsVerified(false);
      markViewAsSessionVerified(null);
      setViewAsError(null);
      setSyncDone(true);
      setAuthError(null);
      return;
    }

    if (syncingRef.current) return;
    syncingRef.current = true;

    log(`Syncing with backend (clerkUserId=${clerkUserId})`);
    try {
      setAuthError(null);
      const { data } = await api.post("/auth/sync");
      log("Backend sync success →", data.user?.email);
      setAppUser(mergeAuthUser(data));
      setViewAs(null);
      setViewAsVerified(false);
      setViewAsError(null);
    } catch (e) {
      log("Backend sync FAILED →", e?.response?.status, e?.message);
      setAppUser(null);
      setAuthError(
        e?.response?.data?.detail ||
        "Your identity was verified, but the app could not finish signing you in."
      );
    } finally {
      setSyncDone(true);
      syncingRef.current = false;
    }
  }, [isLoaded, isSignedIn, clerkUserId]);

  useEffect(() => {
    setSyncDone(false);
    syncWithBackend();
  }, [syncWithBackend]);

  const logout = useCallback(async () => {
    if (getViewAsSessionState()?.token) {
      try { await api.post("/auth/view-as/exit"); }
      catch {
        try { await api.post("/auth/logout"); } catch { /* the session will expire */ }
      }
      clearViewAsSession();
    } else {
      try { await api.post("/auth/logout"); } catch { /* ignore */ }
    }
    await clerk.signOut();
    setAppUser(null);
    setViewAs(null);
    setViewAsVerified(false);
    window.location.href = "/";
  }, [clerk]);

  const refresh = useCallback(async () => {
    try {
      const storedView = getViewAsSessionState();
      if (storedView?.token) {
        const { data } = await loadViewStatus();
        setViewAsSession(storedView.token, data.view_as);
        markViewAsSessionVerified(storedView.token, verifiedViewMode(data.view_as));
        setViewAs(data.view_as);
        setAppUser(data.view_as.target);
        setViewAsVerified(true);
        setViewAsError(null);
        return;
      }
      const { data } = await api.post("/auth/sync");
      setAppUser(mergeAuthUser(data));
      setViewAs(null);
      setViewAsError(null);
    } catch (error) {
      if (getViewAsSessionState()?.token) setViewAsError(viewErrorMessage(error));
      else setAppUser(null);
      markViewAsSessionVerified(null);
      setViewAsVerified(false);
    }
  }, []);

  const startViewAs = useCallback(async (targetUserId, mode = "read_only") => {
    if (!canStoreViewAsSession()) {
      throw new Error("This browser cannot save a tab-local view session.");
    }
    const { data } = await api.post("/auth/view-as/start", {
      target_user_id: targetUserId,
      mode,
    });
    if (!setViewAsSession(data.view_token, data.view_as)) {
      try {
        await api.post("/auth/view-as/exit", {}, {
          headers: { "X-Read-Only-View-Token": data.view_token },
        });
      } catch { /* the server-side session will expire automatically */ }
      throw new Error("Could not save the tab-local view session.");
    }
    markViewAsSessionVerified(data.view_token, verifiedViewMode(data.view_as));
    setViewAs(data.view_as);
    setViewAsVerified(true);
    setAppUser(data.view_as.target);
    setViewAsError(null);
    const target = data.view_as.target;
    window.location.assign(target?.is_parent || target?.role === "parent" ? "/parent-dashboard" : "/dashboard");
  }, []);

  const exitViewAs = useCallback(async ({ redirect = true } = {}) => {
    if (getViewAsSessionState()?.token) await api.post("/auth/view-as/exit");
    clearViewAsSession();
    setViewAs(null);
    setViewAsVerified(false);
    setViewAsError(null);
    setAppUser(null);
    if (redirect) window.location.assign("/admin/users");
  }, []);

  const retrySync = useCallback(() => {
    setSyncDone(false);
    syncWithBackend();
  }, [syncWithBackend]);

  return (
    <AuthContext.Provider value={{
      user: appUser, setUser: setAppUser, loading, logout, refresh,
      clerkConfigured: true, authError, retrySync,
      viewAs, viewAsError, viewAsVerified, startViewAs, exitViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Legacy cookie-based auth provider (fallback when Clerk not configured) ──
export function LegacyAuthProvider({ children }) {
  const [user, setUser] = useState(() => getViewAsSessionState()?.viewAs?.target || null);
  const [viewAs, setViewAs] = useState(() => {
    const stored = getViewAsSessionState();
    return stored?.token ? (stored.viewAs || unavailableView()) : null;
  });
  const [viewAsError, setViewAsError] = useState(null);
  const [viewAsVerified, setViewAsVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const storedView = getViewAsSessionState();
      if (storedView?.token) {
        setViewAsVerified(false);
        if (storedView.viewAs?.target) setUser(storedView.viewAs.target);
        setViewAs(storedView.viewAs || unavailableView());
        const { data } = await loadViewStatus();
        setViewAsSession(storedView.token, data.view_as);
        markViewAsSessionVerified(storedView.token, verifiedViewMode(data.view_as));
        setViewAs(data.view_as);
        setUser(data.view_as.target);
        setViewAsVerified(true);
        setViewAsError(null);
        return;
      }
      const { data } = await api.get("/auth/me");
      setUser(data);
      setViewAs(null);
      setViewAsVerified(false);
      markViewAsSessionVerified(null);
      setViewAsError(null);
    } catch {
      if (getViewAsSessionState()?.token) setViewAsError("The impersonation session could not be verified. Exit it before continuing.");
      else setUser(null);
      setViewAsVerified(false);
      markViewAsSessionVerified(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const logout = useCallback(async () => {
    if (getViewAsSessionState()?.token) {
      try { await api.post("/auth/view-as/exit"); }
      catch {
        try { await api.post("/auth/logout"); } catch { /* the session will expire */ }
      }
      clearViewAsSession();
    } else {
      try { await api.post("/auth/logout"); } catch { /* ignore */ }
    }
    setUser(null);
    setViewAs(null);
    setViewAsVerified(false);
    window.location.href = "/";
  }, []);

  const refresh = useCallback(async () => {
    try {
      const storedView = getViewAsSessionState();
      if (storedView?.token) {
        const { data } = await loadViewStatus();
        setViewAsSession(storedView.token, data.view_as);
        markViewAsSessionVerified(storedView.token, verifiedViewMode(data.view_as));
        setViewAs(data.view_as);
        setUser(data.view_as.target);
        setViewAsVerified(true);
        setViewAsError(null);
        return;
      }
      const { data } = await api.get("/auth/me");
      setUser(data);
      setViewAs(null);
      setViewAsError(null);
    } catch (error) {
      if (getViewAsSessionState()?.token) setViewAsError(viewErrorMessage(error));
      else setUser(null);
      setViewAsVerified(false);
      markViewAsSessionVerified(null);
    }
  }, []);

  const startViewAs = useCallback(async (targetUserId, mode = "read_only") => {
    if (!canStoreViewAsSession()) {
      throw new Error("This browser cannot save a tab-local view session.");
    }
    const { data } = await api.post("/auth/view-as/start", {
      target_user_id: targetUserId,
      mode,
    });
    if (!setViewAsSession(data.view_token, data.view_as)) {
      try {
        await api.post("/auth/view-as/exit", {}, {
          headers: { "X-Read-Only-View-Token": data.view_token },
        });
      } catch { /* the server-side session will expire automatically */ }
      throw new Error("Could not save the tab-local view session.");
    }
    markViewAsSessionVerified(data.view_token, verifiedViewMode(data.view_as));
    setViewAs(data.view_as);
    setViewAsVerified(true);
    setUser(data.view_as.target);
    setViewAsError(null);
    const target = data.view_as.target;
    window.location.assign(target?.is_parent || target?.role === "parent" ? "/parent-dashboard" : "/dashboard");
  }, []);

  const exitViewAs = useCallback(async ({ redirect = true } = {}) => {
    if (getViewAsSessionState()?.token) await api.post("/auth/view-as/exit");
    clearViewAsSession();
    setViewAs(null);
    setViewAsVerified(false);
    setViewAsError(null);
    setUser(null);
    if (redirect) window.location.assign("/admin/users");
  }, []);

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, logout, refresh, clerkConfigured: false,
      authError: null, retrySync: refresh, viewAs, viewAsError, viewAsVerified,
      startViewAs, exitViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
